/**
 * Postage usage (docs/POSTAGE_PLAN.md §4.4): how many letters went on each
 * class, and every member's stamp book in totals. Like the rest of the admin
 * dashboard it never says which pair a stamp was earned or spent on.
 */

import { POSTAGE_LABELS } from '@toiyp/shared';
import { PostageStamp } from '../postage/Stamp';
import { useAdminPostageQuery } from './api';

const CLASSES = ['second', 'first', 'express'] as const;

export function AdminPostage({ enabled }: { enabled: boolean }) {
  const { data, error, isLoading, refetch } = useAdminPostageQuery(undefined, { refetchOnMountOrArgChange: true });
  if (isLoading) return <p className="admin__empty">Loading…</p>;
  if (error || !data) {
    return (
      <p className="admin__empty">
        Couldn’t load postage.{' '}
        <button type="button" className="linkish" onClick={() => void refetch()}>
          Try again
        </button>
      </p>
    );
  }
  const total = CLASSES.reduce((n, c) => n + data.letters[c].all, 0);

  return (
    <div className="admin-postage">
      {!enabled && (
        <p className="admin-postage__off">
          Postage is off — every letter goes 2nd class. Turn it on under App controls.
        </p>
      )}
      <div className="admin-stats">
        {CLASSES.map((c) => (
          <div key={c} className="admin-stat admin-stat--postage">
            <PostageStamp cls={c} size="small" />
            <span className="admin-stat__value">{data.letters[c].all}</span>
            <span className="admin-stat__label">{POSTAGE_LABELS[c]}</span>
            <span className="admin-stat__note">
              {data.letters[c].last30d} in 30 days
              {total > 0 ? ` · ${Math.round((data.letters[c].all / total) * 100)}% of letters` : ''}
            </span>
          </div>
        ))}
        <div className="admin-stat">
          <span className="admin-stat__value">{data.stamps.outstanding}</span>
          <span className="admin-stat__label">Express stamps held</span>
          <span className="admin-stat__note">
            {data.stamps.earned} earned · {data.stamps.spent} spent · {data.stamps.refunded} refunded
          </span>
        </div>
      </div>

      <div className="admin-table__wrap">
        <table className="admin-table">
          <caption className="visually-hidden">Stamp book per member</caption>
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Earned</th>
              <th scope="col">Spent</th>
              <th scope="col">Refunded</th>
              <th scope="col">Held</th>
            </tr>
          </thead>
          <tbody>
            {data.members.length === 0 ? (
              <tr>
                <td colSpan={5} className="admin__empty">
                  No stamps earned yet.
                </td>
              </tr>
            ) : (
              data.members.map((m) => (
                <tr key={m.id}>
                  <td>
                    <span className="admin-table__email">{m.name}</span>
                    <span className="admin-table__sub">{m.handle ? `@${m.handle}` : 'no handle'}</span>
                  </td>
                  <td>{m.earned}</td>
                  <td>{m.spent}</td>
                  <td>{m.refunded}</td>
                  <td>{m.balance}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
